import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useEnabledFeatures } from '../../../../common/hooks/enabled-features/use-enabled-features';
import { useEsriMapStore } from '../../../hooks/use-esri-map-store';
import { LocationAddressAndGpsSection } from '../location-address-and-gps-section';
import { LocationCautionsSection } from '../location-cautions-section';
import { EventAssignedUnitsSection } from './event-assigned-units-section';
import { EventInfoSection } from './event-info-section';
import { EventOverview } from './event-overview';
import { getEventLocation } from '../../../../esri/helpers/locations';
import { getEventPriorityAttributeId } from '../../../../esri/helpers/map-icon-theme';
import {
    useEventNotifications,
    useEventNotificationsStore,
} from '../../../../event-notifications/use-event-notifications';
import { NearbyCautionsSection } from '../nearby-cautions-section';
import {
    getGeoCautionsFromAgencyEventMaterializedCautions,
    getIsUnitAssignedToEvent,
    getNearbyGeoCautionsFromAgencyEventMaterializedCautions,
} from '../helpers';
import { PanelWrapper } from '../panel-wrapper';
import { useApplicationType } from '../../../../common/hooks/use-application-type';
import { useUnitAssignment } from '../../../../unit-assignment/unit-assignment-context';
import { EventFooterSection } from './event-footer-section';
import { isValidLatLng } from '../../../../location/helpers';
import { useSensorsEnabled } from '../../../../sensors/use-sensors-enabled';

export const EventPanel = observer(function EventPanel(): React.ReactElement {
    const mapStore = useEsriMapStore();
    const eventNotificationsStore = useEventNotificationsStore();
    const {
        nearbyCautionsOnMapEnabled,
        dispatchFromMap,
        assignSelfToEvents,
    } = useEnabledFeatures();
    const sensorsEnabled = useSensorsEnabled();
    const { isFirstResponder } = useApplicationType();
    const unitAssignment = useUnitAssignment();

    const selectedMapLedgerEvent = mapStore.selectedMapLedgerEvent;

    const size = isFirstResponder ? 'lg' : 'md';

    const {
        entityId,
        placeName,
        displayLineFirst,
        displayLineSubPremise,
        displayLineOptionalMiddle,
        displayLineLast,
        areCoordinatesVerified,
        locationSource,
        agencyEventMaterializedCautions,
        assignedUnits,
        priority,
        agencyEventNumber,
        cfsCode,
        cfsDescription,
        agencyTypeGlobalAttrId,
    } = selectedMapLedgerEvent || {};

    const mapViewportEvent = entityId ? mapStore.getMapViewportEvent(entityId) : undefined;

    const hasAssignedUnits = !!assignedUnits?.length;

    const geoLocationLatitude = getEventLocation(selectedMapLedgerEvent)?.latitude;
    const geoLocationLongitude = getEventLocation(selectedMapLedgerEvent)?.longitude;

    const eventLocationNotification = useEventNotifications({
        entityId: selectedMapLedgerEvent?.entityId,
        eventId: selectedMapLedgerEvent?.entityId,
        notifications: ['EVENT_LOCATION'],
    });
    const eventHasLocationNotification = !!eventLocationNotification?.length;

    const loggedInUnitId = unitAssignment?.unitId;

    const isCurrentUserUnitAssignedToEvent =
        selectedMapLedgerEvent && loggedInUnitId
            ? getIsUnitAssignedToEvent(selectedMapLedgerEvent, loggedInUnitId)
            : false;

    const showManageUnitsButtonInFooter =
        dispatchFromMap && !!agencyEventNumber && !isFirstResponder;

    const showAssignSelfButtonInFooter =
        isFirstResponder &&
        !!unitAssignment &&
        assignSelfToEvents &&
        !isCurrentUserUnitAssignedToEvent;

    const removeEventNotification = useCallback(() => {
        if (entityId) {
            eventNotificationsStore.removeAllNotificationsForEvent(entityId);
        }
    }, [entityId, eventNotificationsStore]);

    const locationCautions = useMemo(() => {
        const geoCautions = getGeoCautionsFromAgencyEventMaterializedCautions(
            agencyEventMaterializedCautions
        );

        return geoCautions?.filter((caution) => !caution.isNearbyCaution);
    }, [agencyEventMaterializedCautions]);

    const nearbyCautions = useMemo(() => {
        const geoCautions = getNearbyGeoCautionsFromAgencyEventMaterializedCautions(
            agencyEventMaterializedCautions
        );

        return geoCautions?.filter((caution) => caution.isNearbyCaution);
    }, [agencyEventMaterializedCautions]);

    if (nearbyCautionsOnMapEnabled && nearbyCautions) {
        mapStore.setNearbyGeoCautions(nearbyCautions);
    }

    const sortedAssignedUnits = useMemo(() => {
        return assignedUnits
            ?.slice()
            .sort(
                (a, b) =>
                    Number(b.isPrimaryUnitForAgencyEvent) - Number(a.isPrimaryUnitForAgencyEvent)
            );
    }, [assignedUnits]);

    // `OpenSensorGridButton` hides itself when the flag is off or the event has no coordinates,
    // but the footer's own visibility is decided here, so the same condition is needed up front.
    const showOpenSensorGridButton =
        sensorsEnabled && isValidLatLng(geoLocationLatitude, geoLocationLongitude);

    const footerContent = (showManageUnitsButtonInFooter ||
        showAssignSelfButtonInFooter ||
        showOpenSensorGridButton) && (
        <EventFooterSection
            showManageUnitsButtonInFooter={showManageUnitsButtonInFooter}
            showAssignSelfButtonInFooter={showAssignSelfButtonInFooter}
            showOpenSensorGridButton={showOpenSensorGridButton}
            hasAssignedUnits={hasAssignedUnits}
            entityId={entityId}
            latitude={geoLocationLatitude}
            longitude={geoLocationLongitude}
        />
    );

    return (
        <PanelWrapper testId="MAP_EVENT_PANEL" footerContent={footerContent}>
            <EventOverview
                assignedUnits={assignedUnits!}
                priority={priority}
                priorityAttributeId={getEventPriorityAttributeId(
                    mapViewportEvent ?? selectedMapLedgerEvent
                )}
                agencyTypeGlobalAttrId={agencyTypeGlobalAttrId}
                agencyEventNumber={agencyEventNumber}
                cfsCode={cfsCode}
                cfsDescription={cfsDescription}
                entityId={entityId!}
                eventHasLocationNotification={eventHasLocationNotification}
                size={size}
            />
            <LocationAddressAndGpsSection
                placeName={placeName}
                displayLineFirst={displayLineFirst}
                displayLineSubPremises={displayLineSubPremise}
                displayLineOptionalMiddle={displayLineOptionalMiddle}
                displayLineLast={displayLineLast}
                geoLocationLatitude={geoLocationLatitude}
                geoLocationLongitude={geoLocationLongitude}
                areGeoLocationCoordinatesVerified={areCoordinatesVerified}
                source={locationSource}
                eventHasLocationNotification={eventHasLocationNotification}
                removeEventNotification={removeEventNotification}
                size={size}
                clearSelectedNearbyGeoCautionCoordinates={
                    mapStore.clearSelectedNearbyGeoCautionCoordinates
                }
            />
            <EventInfoSection selectedMapLedgerEvent={selectedMapLedgerEvent!} size={size} />
            <LocationCautionsSection cautions={locationCautions} size={size} />
            {nearbyCautionsOnMapEnabled && (
                <NearbyCautionsSection
                    cautions={nearbyCautions}
                    setSelectedNearbyGeoCautionCoordinates={
                        mapStore.setSelectedNearbyGeoCautionCoordinates
                    }
                    size={size}
                />
            )}
            {hasAssignedUnits && (
                <EventAssignedUnitsSection assignedUnits={sortedAssignedUnits} size={size} />
            )}
        </PanelWrapper>
    );
});
